import { apiRequest } from './auth';

// Image generation runs async on the backend (mirrors the mobile create flow): the
// kick-off request returns a generationId immediately (202), so it's fast — a short
// timeout is plenty. The long wait moves into the poll loop below.
export const GENERATION_START_TIMEOUT_MS = 60000;
// Give the background generation up to 8 min to finish (gemini-3-pro-image-preview is slow).
export const GENERATION_POLL_TIMEOUT_MS = 480000;
export const GENERATION_POLL_INTERVAL_MS = 3000;

// Shape of a Generation record returned by GET /api/generations/:id (the fields we read).
export interface PolledGeneration {
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  enhancedImage?: { key: string; url: string };
  originalImages?: Array<{ key: string; url: string }>;
  errorMessage?: string;
}

// The 202 body returned by POST /api/gemini/transform-with-extension (preview path).
export interface GenerationStartResponse {
  success: boolean;
  generationId?: string;
  status?: string;
  originalImageKeys?: string[];
  referenceImageKeys?: string[];
}

// Poll a generation until it reaches a terminal state (completed / failed).
// Mirrors pollGenerationUntilDone() in MobileApp/utils/generationApi.ts — each poll is a
// short GET, so no single request approaches the load balancer's request timeout.
export const pollGenerationUntilDone = async (
  id: string,
  { intervalMs = GENERATION_POLL_INTERVAL_MS, timeoutMs = GENERATION_POLL_TIMEOUT_MS }: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<PolledGeneration> => {
  const deadline = Date.now() + timeoutMs;
  const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

  while (Date.now() < deadline) {
    let generation: PolledGeneration | null = null;
    try {
      // apiRequest unwraps { success, data: { generation } } → { success, generation }.
      const res = await apiRequest<{ success: boolean; generation: PolledGeneration }>(`/api/generations/${id}?size=full`);
      if (res.success) generation = res.generation;
    } catch (err) {
      // Transient network/HTTP error while polling — retry until the deadline.
      console.warn('Polling generation failed, will retry:', err);
    }

    if (generation?.status === 'completed') return generation;
    if (generation?.status === 'failed') throw new Error(generation.errorMessage || 'Generation failed');

    await delay(intervalMs);
  }

  throw new Error('Generation timed out. Please try again.');
};
